import React, { FC } from 'react';
import { useParams } from 'react-router';
import { Grid, Typography } from '@material-ui/core';
import { SeriesScores } from '../../../../config/api/models/Score';
import { ExerciseSeries } from '../../../../config/api/models/Exercise';
import { useI18nPath } from '../../../../config/clientRoutes/hooks';
import { BlankLink, ListItemActionButton } from '../../../shared/components';
import {
  SeriesCardPaper,
  SeriesCardScoresAndProgressInfo,
} from '../TrajectoryDetail/SeriesCard/index';
import { ProgressAndScore } from './types';
import { useTranslation } from 'react-i18next';
import theme from '../../../../config/theme';
import { UrlParams } from '../../../shared/types';
import { StyledLabel } from './Atoms';
import TotalExercisesByLevel from './TotalExercisesByLevel';

type Props = {
  series: ExerciseSeries;
  studentProgressAndScores?: Record<string, SeriesScores>;
};

const getProgressAndScoreBySeries = (
  seriesId: string,
  data: Record<string, SeriesScores>
): ProgressAndScore | undefined => {
  if (!data[seriesId]) return undefined;
  const keys: string[] = Object.keys(data[seriesId]);

  let totalScore: number = 0;
  let totalMaxScore: number = 0;
  let notCompleted: number = 0;

  for (let i = 0; i < keys.length; i++) {
    totalScore += data[seriesId][keys[i]].isAnswered ? data[seriesId][keys[i]].score : 0;
    totalMaxScore += data[seriesId][keys[i]].isAnswered ? data[seriesId][keys[i]].maxScore : 0;
    notCompleted += data[seriesId][keys[i]].isAnswered ? 0 : 1;
  }

  const score = totalMaxScore > 0 && totalScore > 0 ? (totalScore / totalMaxScore) * 100 : 0;
  const scorePercentage = score < 1 ? Math.ceil(score) : Math.floor(score);

  return {
    progress: `${keys.length - notCompleted}/${keys.length}`,
    score: `${scorePercentage}%`,
  };
};

const StudentSeriesItem: FC<Props> = ({ series, studentProgressAndScores }) => {
  const { getPath } = useI18nPath();
  const { t } = useTranslation();
  const { bookId, chapterId, trajectoryId, sectionCode, sectionName } = useParams<UrlParams>();
  const progressAndScore: ProgressAndScore | undefined = !!studentProgressAndScores
    ? getProgressAndScoreBySeries(series.id, studentProgressAndScores)
    : undefined;

  return (
    <Grid item xs={12} sm={6} md={4}>
      <BlankLink
        to={getPath('trajectoryView', {
          bookId,
          chapterId,
          trajectoryId,
          'sectionCode?': sectionCode,
          'sectionName?': sectionName,
          'seriesId?': series.id,
        })}
      >
        <SeriesCardPaper
          className={progressAndScore ? 'show-progress-and-scores' : ''}
          elevation={0}
        >
          <Typography variant="h2">{series.title}</Typography>
          {!!series.blocksOfLevel && (
            <TotalExercisesByLevel series={series}>
              <StyledLabel>{t('trajectory.sections.shared.total')}</StyledLabel>
              {Object.entries(series.blocksOfLevel).map(([key, value]) =>
                value === 0 ? null : (
                  <ListItemActionButton
                    key={key}
                    colorVariant={
                      key === 'Easy'
                        ? theme.config.progressColors.bronze.main
                        : key === 'Average'
                        ? theme.config.progressColors.silver.main
                        : theme.config.progressColors.gold.main
                    }
                    label={value}
                  />
                )
              )}
            </TotalExercisesByLevel>
          )}
          {!!progressAndScore && (
            <SeriesCardScoresAndProgressInfo progressAndScore={progressAndScore} />
          )}
        </SeriesCardPaper>
      </BlankLink>
    </Grid>
  );
};

export default StudentSeriesItem;
